
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CreditCard } from "lucide-react";
import { usePenaltyPayment } from "@/hooks/usePenaltyPayment";

interface PenaltyPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  penaltyAmount: number;
  providerId: string;
  onPaymentSuccess?: () => void;
}

export const PenaltyPaymentModal = ({ 
  open, 
  onOpenChange, 
  penaltyAmount,
  providerId,
  onPaymentSuccess 
}: PenaltyPaymentModalProps) => {
  const { createPenaltyPayment, isPending } = usePenaltyPayment();

  const handlePayPenalty = () => {
    createPenaltyPayment.mutate({
      amount: penaltyAmount,
      currency: 'GBP',
      returnUrl: window.location.origin + '/payment-success?penalty=paid',
      providerId: providerId
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Outstanding Penalty Balance
          </DialogTitle>
          <DialogDescription className="text-left">
            You have an outstanding penalty balance that must be paid before you can complete new bookings.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-amber-800 font-medium">Outstanding Balance:</span>
              <span className="text-amber-900 font-bold text-lg">£{penaltyAmount.toFixed(2)}</span>
            </div>
            <p className="text-amber-700 text-sm mt-2">
              This penalty was applied due to late checkout or other policy violations.
            </p>
          </div>
          
          <div className="text-sm text-gray-600">
            <p>
              Please settle this balance to continue with your booking. You will be redirected to a secure payment page.
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2">
          <Button
            onClick={handlePayPenalty}
            disabled={isPending}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {isPending ? "Processing..." : `Pay £${penaltyAmount.toFixed(2)}`}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
